import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, imgUrl } from "@/lib/api";
import { Trash2, Check, Upload, Image as ImageIcon } from "lucide-react";

export function Messages() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/messages").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.patch(`/admin/messages/${id}`); load(); };
  const remove = async (m) => {
    if (!confirm(`Delete message from ${m.name}? This cannot be undone.`)) return;
    await api.delete(`/admin/messages/${m.id}`);
    toast.success("Message deleted");
    load();
  };
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Messages</h1>
      <div className="overline text-white/40 mb-8">Contact form inbox</div>
      <div className="space-y-3">
        {items.map(m => (
          <div key={m.id} className="surface p-5" data-testid={`msg-${m.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-medium">{m.name} <span className="text-white/40 text-xs">{m.email}</span></div>
                <div className="text-xs text-white/40 font-mono-brand">{m.created_at?.slice(0, 16).replace("T", " ")}</div>
                {m.subject && <div className="text-sm text-white/70 mt-1">Subject: {m.subject}</div>}
                <p className="mt-3 text-sm text-white/80 whitespace-pre-wrap">{m.message}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!m.read && <button onClick={() => markRead(m.id)} data-testid={`message-mark-read-${m.id}`} className="text-xs bg-white/10 px-3 py-1 hover:bg-white/20">Mark read</button>}
                <button onClick={() => remove(m)} data-testid={`message-delete-${m.id}`} title="Delete message"
                  className="text-xs px-2.5 py-1 border border-white/15 text-white/60 hover:border-red-400 hover:text-red-400 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40 text-sm">No messages yet.</p>}
      </div>
    </div>
  );
}

export function Requests() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/custom-requests").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    const form = new FormData(); form.append("status", status);
    await api.patch(`/admin/custom-requests/${id}`, form);
    load();
  };
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Custom Requests</h1>
      <div className="overline text-white/40 mb-8">Commission inquiries</div>
      <div className="space-y-3">
        {items.map(r => (
          <div key={r.id} className="surface p-5" data-testid={`req-${r.id}`}>
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="font-medium">{r.name} <span className="text-white/40 text-xs">{r.email}</span></div>
                <div className="text-xs text-white/40 font-mono-brand">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-white/60">
                  <div>Size: <span className="text-white">{r.desired_size || "—"}</span></div>
                  <div>Budget: <span className="text-white">{r.budget || "—"}</span></div>
                </div>
                <p className="mt-3 text-sm text-white/80 whitespace-pre-wrap">{r.description}</p>
                {r.style_reference && <a href={r.style_reference} target="_blank" rel="noreferrer" className="text-xs text-blue-400 mt-2 inline-block">Reference →</a>}
              </div>
              <select value={r.status} onChange={e => setStatus(r.id, e.target.value)} className="bg-black/40 border border-white/10 px-2 py-1 text-xs">
                <option value="new">New</option>
                <option value="in_discussion">In Discussion</option>
                <option value="accepted">Accepted</option>
                <option value="declined">Declined</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40 text-sm">No requests yet.</p>}
      </div>
    </div>
  );
}

export function Reviews() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/reviews").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const approve = async (id, approved) => { await api.patch(`/admin/reviews/${id}`, { approved }); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/reviews/${id}`); load(); } };
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Reviews</h1>
      <div className="overline text-white/40 mb-8">Moderate customer reviews</div>
      <div className="space-y-3">
        {items.map(r => (
          <div key={r.id} className="surface p-5">
            <div className="flex justify-between gap-4">
              <div className="flex-1">
                <div className="font-medium">{r.name} <span className="text-white/40 text-xs">{r.email}</span> · {r.rating}★</div>
                <p className="mt-2 text-sm text-white/80">{r.comment}</p>
              </div>
              <div className="flex gap-2">
                {!r.approved && <button onClick={() => approve(r.id, true)} data-testid={`approve-${r.id}`} className="bg-emerald-800 text-white px-3 py-1 text-xs"><Check size={14}/></button>}
                {r.approved && <button onClick={() => approve(r.id, false)} className="bg-white/10 text-white/70 px-3 py-1 text-xs">Unapprove</button>}
                <button onClick={() => del(r.id)} className="bg-white/5 text-red-400 px-3 py-1 text-xs"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40 text-sm">No reviews yet.</p>}
      </div>
    </div>
  );
}

export function Media() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ title: "", caption: "", media_type: "image", url: "" });
  const [uploading, setUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // {total, done}
  const [dragging, setDragging] = useState(false);
  const load = () => api.get("/media").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const upload = async (file) => {
    setUploading(true);
    const form = new FormData(); form.append("file", file);
    const { data } = await api.post("/admin/upload", form);
    setF({ ...f, url: data.path, media_type: file.type.startsWith("video") ? "video" : "image" });
    setUploading(false);
  };
  const save = async () => {
    if (!f.url || !f.title) { toast.error("Upload a file and add title"); return; }
    await api.post("/admin/media", f);
    setF({ title: "", caption: "", media_type: "image", url: "" });
    load();
  };
  const del = async (id) => { await api.delete(`/admin/media/${id}`); load(); };

  const bulkUpload = async (fileList) => {
    const files = Array.from(fileList).filter(f =>
      f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (files.length === 0) { toast.error("No images or videos in the selection"); return; }
    setBulkProgress({ total: files.length, done: 0 });
    const form = new FormData();
    files.forEach(file => form.append("files", file));
    try {
      const { data } = await api.post("/admin/media/bulk", form, {
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = evt.loaded / evt.total;
            setBulkProgress({ total: files.length, done: Math.floor(pct * files.length) });
          }
        },
      });
      const ok = data.created.filter(r => r.ok).length;
      const fail = data.created.length - ok;
      if (ok > 0) toast.success(`Added ${ok} item${ok > 1 ? "s" : ""} to Media`);
      if (fail > 0) toast.error(`${fail} file(s) failed`);
      load();
    } catch (e) {
      console.error("Bulk upload failed", e);
      toast.error("Bulk upload failed");
    } finally {
      setBulkProgress(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) bulkUpload(e.dataTransfer.files);
  };

  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Promo Media</h1>
      <div className="overline text-white/40 mb-8">Behind-the-scenes photos & videos</div>

      {/* Drag & drop bulk uploader */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        data-testid="media-dropzone"
        className={`surface border-2 border-dashed p-10 mb-6 text-center transition-colors ${dragging ? "border-white bg-white/5" : "border-white/20"}`}
      >
        <Upload size={28} className="mx-auto mb-3 text-white/50"/>
        <div className="font-serif text-xl mb-2">Drop photos or videos here</div>
        <div className="text-xs text-white/50 mb-4">or</div>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-white text-black px-4 py-2 hover:bg-white/90 uppercase tracking-widest">
          <ImageIcon size={14}/> Choose files
          <input type="file" accept="image/*,video/*" multiple className="hidden" data-testid="media-bulk-file"
            onChange={e => e.target.files?.length && bulkUpload(e.target.files)}/>
        </label>
        {bulkProgress && (
          <div className="mt-6 max-w-md mx-auto">
            <div className="text-xs text-white/60 font-mono-brand mb-2">Uploading {bulkProgress.done}/{bulkProgress.total}…</div>
            <div className="h-1 bg-white/10 overflow-hidden">
              <div className="h-full bg-white transition-all" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }}/>
            </div>
          </div>
        )}
        <div className="text-[10px] text-white/40 mt-4 font-mono-brand">Images & videos · title auto-set from filename · you can edit or remove below</div>
      </div>

      {/* Single manual entry (for cases needing custom titles/captions) */}
      <details className="surface p-5 mb-8">
        <summary className="cursor-pointer text-xs uppercase tracking-widest text-white/60">Add single item with custom title</summary>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <input placeholder="Title" data-testid="media-title" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.title} onChange={e => setF({ ...f, title: e.target.value })}/>
          <input placeholder="Caption" className="bg-black/40 border border-white/10 px-3 py-2 text-sm" value={f.caption} onChange={e => setF({ ...f, caption: e.target.value })}/>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs cursor-pointer bg-white/10 px-3 py-2 hover:bg-white/20">
            <ImageIcon size={14}/> {uploading ? "Uploading…" : "Choose file"}
            <input type="file" accept="image/*,video/*" className="hidden" data-testid="media-file" onChange={e => e.target.files?.[0] && upload(e.target.files[0])}/>
          </label>
          {f.url && <span className="text-xs text-emerald-300 font-mono-brand">Uploaded · {f.media_type}</span>}
          <button onClick={save} disabled={!f.url || !f.title} className="ml-auto bg-white text-black px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-40">Add to Site</button>
        </div>
      </details>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(m => (
          <div key={m.id} className="surface p-2" data-testid={`media-item-${m.id}`}>
            <div className="aspect-square bg-black/40 overflow-hidden mb-2">
              {m.media_type === "video"
                ? <video src={fileUrl(m.url)} className="w-full h-full object-cover" muted loop autoPlay playsInline preload="metadata"/>
                : <img src={imgUrl(fileUrl(m.url), 480)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>}
            </div>
            <div className="text-xs truncate">{m.title}</div>
            <button onClick={() => del(m.id)} className="text-xs text-red-400 mt-1">Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Contacts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then(r => setItems(r.data)); }, []);
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Contacts</h1>
      <div className="overline text-white/40 mb-8">{items.length} unique people</div>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm font-mono-brand">
          <thead className="text-white/50 text-xs uppercase">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Last contact</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.email} className="border-t border-white/5">
                <td className="p-3">{c.name}</td>
                <td className="p-3 text-white/70">{c.email}</td>
                <td className="p-3 text-white/50">{c.phone || "—"}</td>
                <td className="p-3 text-white/50">{c.last_contact?.slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-white/40">No contacts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

